import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import axios from "axios";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AdminDashboard from "@/pages/AdminDashboard";
import MemberDashboard from "@/pages/MemberDashboard";
import StaffDashboard from "@/pages/StaffDashboard";
import LandingPage from "@/pages/LandingPage";
import PaymentPage from "@/pages/PaymentPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import PaymentCancelPage from "@/pages/PaymentCancelPage";
import { Toaster } from "@/components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function RedirectToHome({ user }) {
  const navigate = useNavigate();
  
  useEffect(() => {
    if (user) {
      if (user.role === "super_admin") navigate("/admin", { replace: true });
      else if (user.role === "admin") navigate("/admin", { replace: true });
      else if (user.role === "staff") navigate("/staff", { replace: true });
      else if (user.role === "member" && user.payment_completed === false) {
        // Member hasn't completed payment - redirect to payment
        navigate("/payment", { replace: true });
      }
      else navigate("/dashboard", { replace: true });
    }
    // If no user, stay on landing page (handled by "/" route)
  }, [user, navigate]);
  
  return null;
}

function ProtectedAdmin({ user, onLogout }) {
  const navigate = useNavigate();
  
  useEffect(() => {
    if (!user || !["admin", "super_admin"].includes(user.role)) {
      navigate("/login", { replace: true });
    }
  }, [user, navigate]);
  
  if (!user || !["admin", "super_admin"].includes(user.role)) return null;
  
  return <AdminDashboard user={user} onLogout={onLogout} />;
}

function ProtectedStaff({ user, onLogout }) {
  const navigate = useNavigate();
  
  useEffect(() => {
    if (!user || user.role !== "staff") {
      navigate("/login", { replace: true });
    }
  }, [user, navigate]);
  
  if (!user || user.role !== "staff") return null;
  
  return <StaffDashboard user={user} onLogout={onLogout} />;
}

function ProtectedMember({ user, onLogout }) {
  const navigate = useNavigate();
  
  useEffect(() => {
    if (!user || user.role !== "member") {
      navigate("/login", { replace: true });
    } else if (user.payment_completed === false) {
      // Member hasn't completed payment - redirect to payment
      navigate("/payment", { replace: true });
    }
  }, [user, navigate]);
  
  if (!user || user.role !== "member") return null;
  if (user.payment_completed === false) return null;
  
  return <MemberDashboard user={user} onLogout={onLogout} />;
}

function LoginRoute({ user, setUser }) {
  const navigate = useNavigate();
  
  useEffect(() => {
    if (user) {
      if (user.role === "super_admin") navigate("/admin", { replace: true });
      else if (user.role === "admin") navigate("/admin", { replace: true });
      else if (user.role === "staff") navigate("/staff", { replace: true });
      else if (user.role === "member" && user.payment_completed === false) {
        navigate("/payment", { replace: true });
      }
      else navigate("/dashboard", { replace: true });
    }
  }, [user, navigate]);
  
  if (user) return null;
  
  return <LoginPage setUser={setUser} />;
}

function RegisterRoute({ user, setUser }) {
  const navigate = useNavigate();
  
  useEffect(() => {
    if (user) {
      if (user.role === "super_admin") navigate("/admin", { replace: true });
      else if (user.role === "admin") navigate("/admin", { replace: true });
      else if (user.role === "staff") navigate("/staff", { replace: true });
      else if (user.role === "member" && user.payment_completed === false) {
        navigate("/payment", { replace: true });
      }
      else navigate("/dashboard", { replace: true });
    }
  }, [user, navigate]);
  
  if (user) return null;
  
  return <RegisterPage setUser={setUser} />;
}

function ProtectedPayment({ user }) {
  const navigate = useNavigate();
  
  useEffect(() => {
    if (!user) {
      navigate("/login", { replace: true });
    }
  }, [user, navigate]);
  
  if (!user) return null;
  
  return <PaymentPage user={user} />;
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Validate token and restore session on app load
  useEffect(() => {
    const validateSession = async () => {
      try {
        const token = localStorage.getItem("token");
        const userData = localStorage.getItem("user");
        
        if (token && userData) {
          // Validate token by calling the /auth/me endpoint
          const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          // Token is valid - update user with fresh data from server
          const freshUserData = response.data;
          localStorage.setItem("user", JSON.stringify(freshUserData));
          setUser(freshUserData);
        }
      } catch (error) {
        // Token invalid or expired - clear storage
        console.error("Session validation failed:", error);
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    validateSession();
  }, []);

  // Listen for storage changes (logout from another tab)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "token" && !e.newValue) {
        setUser(null);
      }
    };
    
    // Listen for auth:logout event from axios interceptor
    const handleAuthLogout = () => {
      setUser(null);
    };
    
    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("auth:logout", handleAuthLogout);
    
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("auth:logout", handleAuthLogout);
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={user ? <RedirectToHome user={user} /> : <LandingPage />} />
          <Route path="/login" element={<LoginRoute user={user} setUser={setUser} />} />
          <Route path="/register" element={<RegisterRoute user={user} setUser={setUser} />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/payment" element={<ProtectedPayment user={user} />} />
          <Route path="/payment/success" element={<PaymentSuccessPage />} />
          <Route path="/payment/cancel" element={<PaymentCancelPage />} />
          <Route path="/admin/*" element={<ProtectedAdmin user={user} onLogout={handleLogout} />} />
          <Route path="/staff/*" element={<ProtectedStaff user={user} onLogout={handleLogout} />} />
          <Route path="/dashboard/*" element={<ProtectedMember user={user} onLogout={handleLogout} />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
